'use client'

import { useParams, usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useEffect, useMemo, useRef, useState } from 'react'

import { useCatalogInitialData } from '@/features/catalog/catalog-initial-data'
import { getCatalogUrlParts, normalizeResolvedUrl, useCatalogUrl } from '@/features/catalog/lib/catalog-url-context'

import {
	FilterJsonType,
	FilterProperties,
	FilterPropertyType,
	useGetCatalogFilterQuery,
	useLazyGetCatalogFilterUrlQuery
} from '@/entities/catalog-old'
import { getRegionCookies } from '@/entities/regions'

import { getFilterJsonFromProperties } from '../lib/get-filter-json-properties'

const getAppliedFiltersCount = (appliedFilters: FilterProperties[]) =>
	appliedFilters
		.map(({ VALUES }) => VALUES)
		.flat()
		.filter(({ CHECKED }) => CHECKED).length

const getCategoryCodeFromSlug = (slug: string | string[] | undefined) => (Array.isArray(slug) ? slug[0] : slug)

// Build the products filter JSON on the client from the user's selection, without waiting
// for the server round-trip. Mirrors getFilterJsonFromProperties' shape: the category as
// LIST_SECTION_CODE plus the picked properties. Used for the optimistic products fetch; the
// canonical server value takes over once it arrives (see productsFilterUrl below).
const buildOptimisticProductsFilter = (formFilterUrl: string, categoryCode: string): string => {
	let selection: Record<string, unknown> = {}
	try {
		selection = JSON.parse(formFilterUrl) as Record<string, unknown>
	} catch {
		selection = {}
	}

	return JSON.stringify({
		LIST_SECTION_CODE: { type: FilterJsonType.Default, value: [categoryCode] },
		...selection
	})
}

export const useFilter = () => {
	const router = useRouter()
	const pathname = usePathname()
	const searchParams = useSearchParams()
	const params = useParams()
	const catalogUrlState = useCatalogUrl()
	const routeHasFilter = pathname.includes('/filter/')

	const catalogUrl =
		catalogUrlState?.resolvedUrl ?? `${pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`
	const { pathname: resolvedPathname, search } = getCatalogUrlParts(catalogUrl)
	const resolvedSearchParams = new URLSearchParams(search)
	const effectiveRouteHasFilter = catalogUrlState ? catalogUrlState.pathname.includes('/filter/') : routeHasFilter

	const slugParam = params?.slug as string | string[] | undefined
	const categoryCode = getCategoryCodeFromSlug(slugParam) ?? ''
	const resolvedUrl = catalogUrlState?.resolvedUrl ?? `${resolvedPathname}${search ? `?${search}` : ''}`
	const regionCookies = getRegionCookies()
	const { initialFilterData } = useCatalogInitialData()
	const initialResolvedUrlRef = useRef<string>(resolvedUrl)
	const canUseInitialForCurrentRoute = initialResolvedUrlRef.current === resolvedUrl
	const {
		data,
		currentData,
		isFetching: isFilterFetching
	} = useGetCatalogFilterQuery(
		{
			categoryCode,
			resolvedUrl,
			regionCookies
		},
		// refetchOnFocus is enabled globally on apiSlice; the catalog list/filter shouldn't
		// re-fetch on every tab refocus (wasteful + moves the grid). Disabled on BOTH
		// subscribers of this query (here + useAppliedFilters) so focus-refetch is truly off.
		{ skip: !categoryCode, refetchOnFocus: false }
	)
	// Keep the previous filter panel on screen while a new resolvedUrl refetches.
	// `currentData` goes undefined mid-refetch; falling back to `data` (last
	// successful result for this hook) prevents the panel collapsing to null and
	// flickering. Inputs are already disabled via isLoading during the fetch.
	const filterData = currentData ?? data ?? (canUseInitialForCurrentRoute ? initialFilterData : null)
	const [fetchCatalogFilterUrl] = useLazyGetCatalogFilterUrlQuery()

	// Canonical products filter derived from the SERVER filter response — the source of truth.
	const canonicalFilterUrl = useMemo(() => {
		if (effectiveRouteHasFilter && !filterData) {
			return ''
		}

		const baseJson = getFilterJsonFromProperties(filterData, categoryCode)
		return JSON.stringify(baseJson)
	}, [effectiveRouteHasFilter, filterData, categoryCode])

	// The filter actually sent to the products query. On a filter click we set this
	// OPTIMISTICALLY from the user's selection (see onFilterChange/clearFilter) so products
	// start fetching in parallel with the sef-url + getCatalogFilter round-trips instead of
	// after them (~2 RTT saved). Once the canonical filter for the new URL settles we switch
	// to it: a no-op if it matches the optimistic guess, a self-correcting refetch if not.
	const [productsFilterUrl, setProductsFilterUrl] = useState(canonicalFilterUrl)

	// Remembers the optimistic products filter we set on a click/clear and the resolvedUrl it
	// resolved to (via sef-url). The canonical filter is the same selection re-encoded to the
	// server's ID-form (FILTERED_VALUE); adopting it would refetch IDENTICAL products under a new
	// cache key — a redundant ~1.5s loading cycle that flashes the skeleton/count over already-
	// correct results (the "мигание"). While the optimistic value still represents the current URL
	// we skip that re-encode. Any resolvedUrl we did NOT produce this way (back/forward, direct
	// load, a URL typed in) won't match, so adoption still runs and self-correction is preserved.
	const optimisticForUrlRef = useRef<{ resolvedUrl: string; productsFilter: string } | null>(null)

	useEffect(() => {
		// Only adopt canonical when fresh data for the CURRENT arg is loaded (currentData is
		// undefined mid-refetch). This keeps the optimistic value on screen while filter refetches.
		if (currentData && !isFilterFetching) {
			const optimistic = optimisticForUrlRef.current
			if (optimistic && optimistic.resolvedUrl === resolvedUrl && optimistic.productsFilter === productsFilterUrl) {
				// Optimistic value already represents this URL → re-encoding to ID-form is a no-op
				// fetch. Skip it to avoid the redundant products refetch and its skeleton flash.
				return
			}
			setProductsFilterUrl(canonicalFilterUrl)
		}
	}, [currentData, isFilterFetching, canonicalFilterUrl, resolvedUrl, productsFilterUrl])

	const appliedFiltersCount = useMemo(() => {
		return getAppliedFiltersCount(filterData?.PROPERTIES || [])
	}, [filterData?.PROPERTIES])

	const serverPriceFilter = useMemo(() => {
		const priceProp = filterData?.PROPERTIES?.find(p => p.PROPERTY_TYPE === FilterPropertyType.Number)
		if (!priceProp) return undefined

		const checkedValues = priceProp.VALUES.filter(v => v.CHECKED)
		if (checkedValues.length === 0) return undefined

		return {
			type: FilterJsonType.Price,
			value: checkedValues.map(v => v.FILTERED_VALUE ?? v.VALUE)
		}
	}, [filterData?.PROPERTIES])

	const onFilterChange = async (filterUrl: string) => {
		if (!categoryCode) return

		// Fire products immediately with the clicked selection — parallel to the URL/filter refresh.
		const optimisticFilter = buildOptimisticProductsFilter(filterUrl, categoryCode)
		setProductsFilterUrl(optimisticFilter)

		const filterUrlRes = await fetchCatalogFilterUrl({ categoryCode, filterUrl }, true)
		const filterSefUrl = filterUrlRes?.data

		if (filterSefUrl) {
			const currentSort = resolvedSearchParams.get('sort')
			let nextUrl = filterSefUrl

			if (currentSort) {
				try {
					const normalizedUrl = new URL(filterSefUrl, window.location.origin)
					normalizedUrl.searchParams.set('sort', currentSort)
					nextUrl = `${normalizedUrl.pathname}${normalizedUrl.search}`
				} catch {
					nextUrl = `${filterSefUrl}${filterSefUrl.includes('?') ? '&' : '?'}sort=${encodeURIComponent(currentSort)}`
				}
			}

			const normalizedNextUrl = normalizeResolvedUrl(nextUrl)
			// The optimistic filter provably corresponds to this URL (the URL was derived from it),
			// so the upcoming canonical adoption for this resolvedUrl would be a redundant refetch.
			optimisticForUrlRef.current = { resolvedUrl: normalizedNextUrl, productsFilter: optimisticFilter }
			if (catalogUrlState?.updateResolvedUrl) {
				catalogUrlState.updateResolvedUrl(normalizedNextUrl)
			} else {
				await router.replace(normalizedNextUrl)
			}
		} else {
			// sef-url failed → the URL/filter won't change, so roll the optimistic products
			// filter back to the current canonical state to keep products/URL/panel consistent.
			setProductsFilterUrl(canonicalFilterUrl)
		}
	}

	const clearFilter = async () => {
		if (!categoryCode) return

		// Optimistically show the unfiltered section list immediately (just the category).
		const optimisticFilter = buildOptimisticProductsFilter(JSON.stringify({}), categoryCode)
		setProductsFilterUrl(optimisticFilter)

		const filterUrlRes = await fetchCatalogFilterUrl({ categoryCode, filterUrl: JSON.stringify({}) }, true)
		const filterSefUrl = filterUrlRes?.data

		if (filterSefUrl) {
			const normalizedNextUrl = normalizeResolvedUrl(filterSefUrl)
			// Same as onFilterChange: the optimistic (empty) filter already matches the root URL,
			// so skip the redundant canonical adoption/refetch for it.
			optimisticForUrlRef.current = { resolvedUrl: normalizedNextUrl, productsFilter: optimisticFilter }
			if (catalogUrlState?.updateResolvedUrl) {
				catalogUrlState.updateResolvedUrl(normalizedNextUrl)
			} else {
				await router.replace(normalizedNextUrl)
			}
		} else {
			// sef-url failed → roll the optimistic products filter back to the canonical state.
			setProductsFilterUrl(canonicalFilterUrl)
		}
	}

	return {
		filterData: filterData,
		properties: filterData?.PROPERTIES,
		title: filterData?.SEO?.SECTION_PAGE_TITLE,
		seo: filterData?.SEO,
		filterFaq: filterData?.FAQ,
		onFilterChange,
		clearFilter,
		appliedFiltersCount,
		isLoading: isFilterFetching,
		filterUrl: productsFilterUrl,
		serverPriceFilter
	}
}
